import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import {
  Breadcrumb,
  Row,
  Col,
  Card,
  notification,
  Tag,
  Form,
  message,
  Input,
  Upload,
  Button,
  Select,
} from "antd";
import { PlusOutlined, FileOutlined, CopyOutlined } from "@ant-design/icons";
import axios from "axios";
import { useParams } from "react-router-dom";
import { formatDateTime } from "../../helpers/date";
import IssueStatusTag from "../shared/IssueStatusTag";
import { ISSUE_STATUSES, ISSUE_CATEGORIES } from "../../constants/issue";
import config from "../../config/app";
import { previewBase64Image } from "../../helpers/image";

const routes = [
  { path: "", breadcrumbName: "App" },
  { path: "", breadcrumbName: "Support Tickets" },
  { path: "", breadcrumbName: "Ticket Details" },
];

const SupportTicketDetail = () => {
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [form] = Form.useForm();
  const { id } = useParams();
  const history = useHistory();

  const fetchTicket = useCallback(async () => {
    setLoading(true);

    try {
      const {
        data: { data: ticket },
      } = await axios.get(`/api/issues/${id}`);

      setTicket(ticket);
    } catch (error) {
      notification.error({
        message: `Error`,
        description: error.response
          ? error.response.data.message
          : error.message,
      });
    }

    setLoading(false);
  }, [id]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleOpenAttachment = (issueId, attachment) => {
    axios
      .post(`/issues/${issueId}/attachment`, { attachment })
      .then((res) => window.open(res.data.data.url, "_blank"));
  };

  const handleCopyAttachmentLink = (issueId, attachment) => {
    const s3Key = `${config.s3Folder.issueAttachment}/${issueId}/${attachment}`;

    // Generate signed CloudFront link for attachment
    axios
      .post(`api/storage/generate-public-link`, { s3Key, validDays: 90 })
      .then((res) => {
        navigator.clipboard.writeText(res.data.data.url);

        message.success(
          "Share Link generated and copied to clipboard (Valid for 90 days)",
          10
        );
      })
      .catch((err) => {
        message.error("Error generating share link");
      });
  };

  const addCommentHandler = async (newComment) => {
    try {
      await axios.post(
        `/api/issues/${ticket?.objectId}/comments`,
        {
          ...newComment,
          attachments: uploadedFiles,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          formSerializer: {
            indexes: null,
          },
        }
      );

      form.resetFields();
      fetchTicket();
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }
  };

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">Support Ticket</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "10px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      {ticket && (
        <div>
          <Row justify="center" align="middle">
            <Col span={12}>
              <Card
                title={
                  <span>
                    <strong>Ticket ID #{ticket.ticketId}</strong>
                  </span>
                }
                bordered={true}
              >
                <p>
                  <strong>Ticket ID: </strong>
                  <br />
                  <Tag color="blue">{ticket.ticketId}</Tag>
                </p>
                <p>
                  <strong>Submitted At: </strong>
                  <br />
                  {formatDateTime(ticket.createdAt)}
                </p>
                <p>
                  <strong>Submitted By: </strong>
                  <br />
                  {ticket?.createdBy?.email || "N/A"}
                </p>
                <p>
                  <strong>Tracking No: </strong>
                  <br />
                  {ticket.order?.consign_no}
                </p>
                <p>
                  <strong>User / Merchant: </strong>
                  <br />
                  {ticket.user?.email}
                </p>
                <p>
                  <strong>Bank Details: </strong>
                  <br />
                  {`${ticket?.bankDetails?.bankHolderName} (${ticket?.bankDetails?.bankName} ${ticket?.bankDetails?.bankAccount})`}
                </p>
                <p>
                  <strong>Assignee: </strong>
                  <br />
                  {ticket.assignedTo?.username || "N/A"}
                </p>
                <p>
                  <strong>Issue Category: </strong>
                  <br />
                  <Tag color={ISSUE_CATEGORIES[ticket.category]?.color || "grey"}>{ISSUE_CATEGORIES[ticket.category]?.label || ticket.category}</Tag>
                </p>
                <p>
                  <strong>Status: </strong>
                  <br />
                  <IssueStatusTag statusKey={ticket.status} />
                </p>
                <p>
                  <strong>Description: </strong>
                  <br />
                  <span className="whitespace-pre-line">{ticket.remark}</span>
                </p>
                <p>
                  <strong>Attachments: </strong>
                  <br />
                  <ol>
                    {ticket.attachments.map((attachment) => (
                      <li key={attachment}>
                        {attachment}
                        {` `}
                        <Button
                          size="small"
                          onClick={() =>
                            handleOpenAttachment(ticket.objectId, attachment)
                          }
                        >
                          <FileOutlined />
                          Open
                        </Button>
                        {` `}
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => {
                            handleCopyAttachmentLink(
                              ticket.objectId,
                              attachment
                            );
                          }}
                        >
                          Copy Link
                        </Button>
                      </li>
                    ))}
                  </ol>
                </p>
              </Card>
            </Col>
          </Row>

          <Row className="mt-2" justify="center" align="middle">
            <Col span={12}>
              <Card>
                {!ticket.comments?.length && "No comments added yet"}
                {ticket.comments.map((comment) => (
                  <Row>
                    <Col span={22} offset={comment.attendedBy ? 2 : 0}>
                      <Card
                        title={
                          <span>
                            {comment.attendedBy
                              ? `Admin (${comment.attendedBy?.username})`
                              : "User"}
                            <br />
                            <span className="text-xs">
                              {formatDateTime(comment.createdAt)}
                            </span>
                          </span>
                        }
                      >
                        <p className="whitespace-pre-line">{comment.remark}</p>
                        <p className="text-xs">Attachment:</p>
                        <Row>
                          {!comment.attachments?.length && "-"}
                          <ol>
                            {comment.attachments.map((attachment) => (
                              <li key={attachment}>
                                {attachment}
                                {` `}
                                <Button
                                  size="small"
                                  onClick={() =>
                                    handleOpenAttachment(
                                      comment.objectId,
                                      attachment
                                    )
                                  }
                                >
                                  <FileOutlined />
                                  Open
                                </Button>
                                {` `}
                                <Button
                                  icon={<CopyOutlined />}
                                  size="small"
                                  onClick={() => {
                                    handleCopyAttachmentLink(
                                      comment.objectId,
                                      attachment
                                    );
                                  }}
                                >
                                  Copy Link
                                </Button>
                              </li>
                            ))}
                          </ol>
                        </Row>
                        {comment.status && (
                          <div>
                            <p className="text-xs">Status:</p>
                            <IssueStatusTag statusKey={comment.status} />
                          </div>
                        )}
                      </Card>
                    </Col>
                  </Row>
                ))}
              </Card>
            </Col>
          </Row>

          <Row className="mt-2" justify="center" align="middle">
            <Col span={12}>
              <Card title="Add Comment">
                <Form
                  form={form}
                  name="add-issue-comment-form"
                  autoComplete="off"
                  onFinish={addCommentHandler}
                >
                  <Form.Item
                    label="Remark"
                    name="remark"
                    rules={[{ required: true }]}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <Form.Item label="Attachments" name="attachments">
                    <Upload
                      beforeUpload={() => false}
                      listType="picture-card"
                      onChange={(e) => {
                        setUploadedFiles((prev) => [...prev, e.file]);
                      }}
                      onPreview={(file) => previewBase64Image(file)}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    </Upload>
                  </Form.Item>

                  <Form.Item label="Status" name="status">
                    <Select
                      defaultValue={null}
                      style={{ width: 300 }}
                      placeholder="Ignore if no change"
                    >
                      {ISSUE_STATUSES.map((status) => (
                        <Select.Option value={status.key}>
                          {status.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Row justify="space-between">
                    <Form.Item>
                      <Button
                        loading={loading}
                        type="primary"
                        htmlType="submit"
                      >
                        + Add
                      </Button>
                    </Form.Item>

                    <Button
                      type="button"
                      size="middle"
                      align="right"
                      onClick={() => history.goBack()}
                    >
                      Back
                    </Button>
                  </Row>
                </Form>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default SupportTicketDetail;
